import React, { useMemo, useState } from "react";
import { Card, Table, Row, Col, Select, Statistic, Button, Tag, Space } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DownloadOutlined } from "@ant-design/icons";
import {
    ResponsiveContainer,
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    LineChart,
    Line,
    Legend,
    PieChart,
    Pie,
    Cell,
} from "recharts";
import SmallProfileCard from "../../../components/ContactInfo/SmallProfileCard";
import { constants } from "../../../constants/common";
import dayjs from "dayjs";

type DeveloperRow = {
    key: string;
    developerId: string;
    name: string;
    employeeId: string;
    email: string;
    month: string;
    firstName: string;
    lastName: string;
    avatar?: string;
    proratedSalary: number;
};

const parseAmount = (v: string | number) =>
    Number(String(v).replace(/,/g, "")) || 0;
const fmt = (v: number) =>
    v.toLocaleString(undefined, { maximumFractionDigits: 2 });

// helper to sort month strings like "Jun-25"
const sortMonths = (arr: { month: string;[key: string]: any }[]) => {
    return arr
        .map((item) => ({
            ...item,
            date: dayjs(item.month, "MMM-YY").toDate(),
        }))
        .sort((a, b) => a.date.getTime() - b.date.getTime())
        .map((item) => ({
            ...item,
            month: dayjs(item.date).format("MMM-YY"), // normalize display
        }));
};

// ---------------- Component ----------------
export default function SalaryAnalytics({ sampleData }) {
    const [monthFilter, setMonthFilter] = useState<string | "all">("all");

    // flatten developers
    const rows: DeveloperRow[] = useMemo(() => {
        const out: DeveloperRow[] = [];
        for (const m of sampleData) {
            for (const d of m.developers) {
                out.push({
                    key: `${m.month}_${d.developerId}_${d.employeeId}_${Math.random()}`,
                    developerId: d.developerId,
                    name: `${d.firstName} ${d.lastName}`.trim(),
                    firstName: d.firstName,
                    lastName: d.lastName,
                    employeeId: d.employeeId,
                    email: d.email,
                    month: m.month,
                    proratedSalary: parseAmount(d.proratedSalary),
                });
            }
        }
        return out;
    }, [sampleData]);

    const months = useMemo(
        () => ["all", ...sampleData.map((s) => s.month)],
        [sampleData]
    );

    const filtered = useMemo(() => {
        return monthFilter === "all"
            ? rows
            : rows.filter((r) => r.month === monthFilter);
    }, [rows, monthFilter]);

    const totalsByMonth = useMemo(() => {
        const rows = sampleData.map((m) => ({
            month: m.month,
            total: m.developers.reduce(
                (s, d) => s + parseAmount(d.proratedSalary),
                0
            ),
        }));
        return sortMonths(rows);
    }, [sampleData]);

    const topDevelopers = useMemo(() => {
        const map = new Map<string, { name: string; amount: number }>();
        for (const r of rows) {
            const key = r.developerId + "::" + r.name;
            const prev = map.get(key) || { name: r.name, amount: 0 };
            prev.amount += r.proratedSalary;
            map.set(key, prev);
        }
        return Array.from(map.values())
            .sort((a, b) => b.amount - a.amount)
            .slice(0, 8);
    }, [rows]);

    const totalAcross = useMemo(
        () => rows.reduce((s, r) => s + r.proratedSalary, 0),
        [rows]
    );

    const totalForSelectedMonth = useMemo(() => {
        if (monthFilter === "all") return null;
        return filtered.reduce((s, r) => s + r.proratedSalary, 0);
    }, [filtered, monthFilter]);

    // Developer trend line data
    const developerTrend = useMemo(() => {
        const monthRows: any[] = sampleData.map((m) => {
            const entry: any = { month: m.month };
            for (const d of m.developers) {
                const devName = `${d.firstName} ${d.lastName}`.trim();
                const val = parseAmount(d.proratedSalary);
                entry[devName] = (entry[devName] || 0) + val; // ✅ accumulate instead of overwrite
            }
            return entry;
        });

        // sort by month properly
        const sorted = sortMonths(monthRows);

        const devNames = Array.from(new Set(rows.map((r) => r.name)));
        return { data: sorted, devNames };
    }, [rows, sampleData]);

    // ---------------- Table ----------------
    const columns: ColumnsType<DeveloperRow> = [
        {
            title: "Employee",
            dataIndex: "name",
            key: "name",
            render: (value, records) => <SmallProfileCard
                name={constants.GET_FULL_NAME(records)}
                avatar={records?.avatar}
                subTitle={records?.employeeId || "N/A"}
            />,
            sorter: (a, b) => a.name.localeCompare(b.name),
        },
        { title: "Email", dataIndex: "email", key: "email" },
        {
            title: "Month",
            dataIndex: "month",
            key: "month",
            filters: sampleData.map((s) => ({ text: s.month, value: s.month })),
            onFilter: (value, record) => record.month === value,
        },
        {
            title: "Prorated Salary",
            dataIndex: "proratedSalary",
            key: "proratedSalary",
            sorter: (a, b) => a.proratedSalary - b.proratedSalary,
            render: (v: number) => <b>₹ {fmt(v)}</b>,
        },
    ];

    // ---------------- CSV export ----------------
    const downloadCSV = () => {
        const header = [
            "developerId",
            "name",
            "employeeId",
            "email",
            "month",
            "proratedSalary",
        ];
        const lines = [header.join(",")];
        for (const r of filtered) {
            lines.push(
                [
                    r.developerId,
                    `"${r.name.replace(/"/g, '""')}"`,
                    r.employeeId,
                    r.email,
                    r.month,
                    r.proratedSalary.toFixed(2),
                ].join(",")
            );
        }
        const blob = new Blob([lines.join("\n")], {
            type: "text/csv;charset=utf-8;",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `salary_analytics_${monthFilter}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const COLORS = [
        "#8884d8",
        "#82ca9d",
        "#ffc658",
        "#ff7f50",
        "#a4de6c",
        "#d0ed57",
        "#8dd1e1",
        "#83a6ed",
    ];

    return (
        <div className="py-4 space-y-6">
            <div className="flex items-center justify-between">
                <h2 className="text font-semibold">Salary Analytics</h2>
                <Space>
                    <Select
                        value={monthFilter}
                        onChange={(v) => setMonthFilter(v)}
                        style={{ width: 160 }}
                    >
                        {months.map((m) => (
                            <Select.Option key={m} value={m}>
                                {m === "all" ? "All months" : m}
                            </Select.Option>
                        ))}
                    </Select>
                    <Button
                        icon={<DownloadOutlined />}
                        onClick={downloadCSV}
                        type="primary"
                    >
                        Export CSV
                    </Button>
                </Space>
            </div>

            <Row gutter={16} className="items-stretch">
                <Col span={6}>
                    <Card className="rounded  h-full">
                        <Statistic title="Total (all months)" value={`₹ ${fmt(totalAcross)}`} />
                        {totalForSelectedMonth !== null && (
                            <Statistic
                                title={`Total for ${monthFilter}`}
                                value={`₹ ${fmt(totalForSelectedMonth)}`}
                            />
                        )}
                        <div className="mt-4">
                            <Tag color="blue">Months: {sampleData.length}</Tag>
                            <Tag color="green">
                                Developers: {new Set(rows.map((r) => r.developerId)).size}
                            </Tag>
                        </div>
                    </Card>
                </Col>

                <Col span={10}>
                    <Card className="rounded  h-full">
                        <h4 className="mb-2">Monthly Total (bar)</h4>
                        <div style={{ width: "100%", height: 220 }}>
                            <ResponsiveContainer>
                                <BarChart
                                    data={totalsByMonth}
                                    margin={{ top: 10, right: 20, left: 0, bottom: 0 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" />
                                    <XAxis dataKey="month" />
                                    <YAxis />
                                    <Tooltip formatter={(val: any) => `₹ ${fmt(val)}`} />
                                    <Bar dataKey="total" name="Total" fill="#8884d8" />
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </Col>

                <Col span={8}>
                    <Card className="rounded  h-full">
                        <h4 className="mb-2">Top Developers (by total)</h4>
                        <div style={{ width: "100%", height: 220 }}>
                            <ResponsiveContainer>
                                <PieChart>
                                    <Pie
                                        data={topDevelopers}
                                        dataKey="amount"
                                        nameKey="name"
                                        outerRadius={80}
                                    >
                                        {topDevelopers.map((_, idx) => (
                                            <Cell key={`c-${idx}`} fill={COLORS[idx % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip formatter={(val: any) => `₹ ${fmt(val)}`} />
                                    <Legend />
                                </PieChart>
                            </ResponsiveContainer>
                        </div>
                    </Card>
                </Col>
            </Row>

            <Card className="rounded ">
                <h4 className="mb-4">Developers detail</h4>
                <Table
                    columns={columns}
                    dataSource={filtered}
                    pagination={{ pageSize: 8 }}
                />
            </Card>

            <Card className="rounded ">
                <h4 className="mb-4">Developer Trends (line by month)</h4>
                <div style={{ width: "100%", height: 400 }}>
                    <ResponsiveContainer>
                        <LineChart
                            data={developerTrend.data}
                            margin={{ top: 20, right: 30, left: 0, bottom: 0 }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis dataKey="month" />
                            <YAxis />
                            <Tooltip formatter={(v: any) => `₹ ${fmt(v)}`} />
                            <Legend />
                            {developerTrend.devNames.map((dev, idx) => (
                                <Line
                                    key={dev}
                                    type="monotone"
                                    dataKey={dev}
                                    stroke={COLORS[idx % COLORS.length]}
                                    strokeWidth={2}
                                />
                            ))}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            </Card>
        </div>
    );
}
